import { useMemo, useState } from "react";
import { ChevronDown, FileMinus2, FilePen, FilePlus2 } from "lucide-react";
import { useLocale } from "@ncp/i18n";
import { cn } from "@ncp/ui";
import type { PageFileChange } from "./types";

type Line = { kind: "same" | "add" | "del" | "gap"; text: string; a?: number; b?: number };

const MAX_CELLS = 4_000_000;

/** A line diff (longest common subsequence) with three lines of context around each change; very large files show only their sizes. */
export function diffLines(before: string, after: string, context = 3): Line[] | null {
  const a = before.split("\n");
  const b = after.split("\n");
  if (a.length * b.length > MAX_CELLS) return null;
  const n = a.length, m = b.length;
  const lcs = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  const all: Line[] = [];
  let i = 0, j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) { all.push({ kind: "same", text: a[i], a: i + 1, b: j + 1 }); i++; j++; }
    else if (j < m && (i >= n || lcs[i][j + 1] >= lcs[i + 1][j])) { all.push({ kind: "add", text: b[j], b: j + 1 }); j++; }
    else { all.push({ kind: "del", text: a[i], a: i + 1 }); i++; }
  }
  const keep = new Set<number>();
  all.forEach((l, k) => { if (l.kind !== "same") for (let d = -context; d <= context; d++) keep.add(k + d); });
  const out: Line[] = [];
  let skipped = 0;
  all.forEach((l, k) => {
    if (keep.has(k)) {
      if (skipped) { out.push({ kind: "gap", text: String(skipped) }); skipped = 0; }
      out.push(l);
    } else skipped++;
  });
  if (skipped && out.length) out.push({ kind: "gap", text: String(skipped) });
  return out;
}

function FileDiff({ change }: { change: PageFileChange }) {
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [open, setOpen] = useState(true);
  const lines = useMemo(() => (change.binary ? null : diffLines(change.before ?? "", change.after ?? "")), [change]);
  const Icon = change.change === "added" ? FilePlus2 : change.change === "removed" ? FileMinus2 : FilePen;
  const added = lines?.filter((l) => l.kind === "add").length ?? 0;
  const removed = lines?.filter((l) => l.kind === "del").length ?? 0;
  return (
    <li className="overflow-hidden rounded-lg border border-sand-200 bg-white">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2 text-start">
        <Icon className={cn("size-4 shrink-0", change.change === "added" ? "text-success-600" : change.change === "removed" ? "text-danger-600" : "text-aegold-700")} aria-hidden />
        <span className="min-w-0 flex-1 truncate font-mono text-sm" dir="ltr">{change.path}</span>
        {lines ? <span className="font-mono text-xs tabular-nums" dir="ltr"><span className="text-success-700">+{added}</span> <span className="text-danger-700">−{removed}</span></span> : null}
        <ChevronDown className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      {open ? (
        change.binary ? <p className="border-t border-sand-100 px-3 py-2 text-xs text-muted-foreground">{ar ? "ملف ثنائي (صورة أو خط أو بيانات)." : "A binary file (an image, a font or data)."}</p>
        : !lines ? <p className="border-t border-sand-100 px-3 py-2 text-xs text-muted-foreground">{ar ? "الملف أكبر من أن يعرض الفرق." : "The file is too large to show the difference."}</p>
        : (
          <div className="max-h-[28rem] overflow-auto border-t border-sand-100" dir="ltr">
            <table className="w-full border-collapse font-mono text-xs leading-5">
              <tbody>
                {lines.map((l, k) => l.kind === "gap" ? (
                  <tr key={k} className="bg-sand-50 text-muted-foreground"><td colSpan={3} className="px-3 py-0.5">⋯ {l.text} {ar ? "سطر دون تغيير" : "unchanged lines"}</td></tr>
                ) : (
                  <tr key={k} className={l.kind === "add" ? "bg-success-50" : l.kind === "del" ? "bg-danger-50" : undefined}>
                    <td className="w-10 select-none px-2 text-end text-sand-400 tabular-nums">{l.a ?? ""}</td>
                    <td className="w-10 select-none px-2 text-end text-sand-400 tabular-nums">{l.b ?? ""}</td>
                    <td className="whitespace-pre-wrap break-all px-2"><span className="select-none text-sand-500">{l.kind === "add" ? "+ " : l.kind === "del" ? "− " : "  "}</span>{l.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </li>
  );
}

/** The files a suggestion adds, changes or removes against the version it was made from. */
export function FileChanges({ changes, className }: { changes: PageFileChange[]; className?: string }) {
  const { locale } = useLocale();
  if (changes.length === 0) return <p className={cn("text-sm text-muted-foreground", className)}>{locale === "ar" ? "لا تغييرات." : "No changes."}</p>;
  return <ul className={cn("space-y-2", className)}>{changes.map((c) => <FileDiff key={c.path} change={c} />)}</ul>;
}
